import { Injectable, ViewContainerRef } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';
import * as urltemplate from 'url-template';
import { LMActionService } from '@services/lm-action.service';
import { LMTransactionService, LMTransactionState } from '@transaction/index';

@Injectable()
export class LMApplicationMenuTransactionService {
  constructor(
    private lmActionService: LMActionService,
    private lmTransactionService: LMTransactionService,
  ) {}

  private createTransactionUrl(transactionUrl: string, actionSpec: LMActionSpec, dataView: LMDataView) {
    return urltemplate.parse(transactionUrl).expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    });
  }

  private runActionChecks(
    transactionState: LMTransactionState,
    actionUrlTemplate: string,
    busClassSpec: LMBusClassSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    placeholder: ViewContainerRef
  ): Observable<boolean> {
    return this.lmTransactionService.getScheduleView(actionSpec, busClassSpec.name).pipe(
      map((scheduleView: LMDataView) => {
        if (!scheduleView) {
          // Cancelled scheduling
          transactionState.cancel();
          return false;
        }
        const transactionRequest = this.lmActionService.buildActionRequestForDataView(
          dataView,
          actionSpec,
          null,
          null,
          null,
          scheduleView
        );
        transactionState.setRequestPayload([ transactionRequest ]);
        return true;
      }),
      // proceed if schedule is not cancelled; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transactionState, busClassSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transactionState)),
      catchError(this.lmTransactionService.checkCancelled)
    );
  }

  public runMenuAction(
    transactionUrlTemplate: string,
    busClassSpec: LMBusClassSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    placeholder: ViewContainerRef
  ): Observable<LMTransactionState> {
    const transactionState: LMTransactionState = new LMTransactionState(this.createTransactionUrl(transactionUrlTemplate, actionSpec, dataView));
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [dataView], actionSpec });
    transactionState.setRequestPayload(transactionRequests);
    return this.runActionChecks(
      transactionState,
      transactionUrlTemplate,
      busClassSpec,
      actionSpec,
      dataView,
      placeholder
    ).pipe(
      flatMap((proceed: boolean) => proceed ?
        this.lmTransactionService.executeAction(transactionState) :
        of(transactionState)
      )
    );
  }
}
